/**
 * Where every card goes: the route layout on the map graph
 * (specs/workshop-modules/impact_path.md → "Route layout").
 *
 *   left → right   causality and time: start, input, output, outcome, impact,
 *                  goals, DONE. A column means the same kind in every lane, and
 *                  a child is always right of the parent it hangs from.
 *   top → bottom   parallel strands: one lane per scenario, and in a lane every
 *                  subtree keeps its own band of rows, above or below the start.
 *
 * Pure: graph-derived input in, column and centre-y per card out.
 */
import { cardHeightFor } from './sceneConfig';

/**
 * The tree each lane hangs from: a node's primary edge from its own scenario
 * (its start or a node of the same scenario). Goals are pinned, so nothing
 * hangs from or into them; a blocker on an edge is placed on that edge.
 * Returns parentOf, the ordered children, and the band tops per lane.
 */
export function laneTrees(lanes, nodesByLane, primaryInto, isPinned, isPlacedBlocker) {
  const parentOf = new Map();
  const children = new Map();
  const add = (p, c) => { if (!children.has(p)) children.set(p, []); children.get(p).push(c); };
  const topsByLane = new Map();
  lanes.forEach((lane) => {
    const members = (nodesByLane.get(lane.id) || []).filter((n) => !isPinned(n.id) && !isPlacedBlocker(n.id));
    const memberIds = new Set(members.map((n) => n.id));
    members.forEach((n) => {
      const e = primaryInto.get(n.id);
      const p = e?.attrs.from;
      if (p && (p === lane.id || memberIds.has(p))) parentOf.set(n.id, p);
    });
    // Reach from the start; whatever is left (an orphan chain, a cycle) gets
    // a band of its own from the node whose parent chain breaks.
    const reached = new Set();
    const tops = [];
    const kidsOf = new Map();
    members.forEach((n) => {
      const p = parentOf.get(n.id);
      if (p) { if (!kidsOf.has(p)) kidsOf.set(p, []); kidsOf.get(p).push(n.id); }
    });
    const mark = (id) => {
      const stack = [id];
      while (stack.length) {
        const cur = stack.pop();
        if (reached.has(cur)) continue;
        reached.add(cur);
        (kidsOf.get(cur) || []).forEach((k) => stack.push(k));
      }
    };
    (kidsOf.get(lane.id) || []).forEach((k) => { tops.push(k); mark(k); });
    members.forEach((n) => {
      if (reached.has(n.id)) return;
      let cur = n.id;
      const seen = new Set();
      while (parentOf.has(cur) && parentOf.get(cur) !== lane.id && !seen.has(cur)) {
        seen.add(cur);
        const p = parentOf.get(cur);
        if (seen.has(p) || reached.has(p)) break;
        cur = p;
      }
      parentOf.delete(cur);
      tops.push(cur);
      mark(cur);
    });
    // Ordered children for every reached node, in graph order, without the broken links.
    members.forEach((n) => {
      const p = parentOf.get(n.id);
      if (p && p !== lane.id) add(p, n.id);
    });
    topsByLane.set(lane.id, tops);
  });
  return { parentOf, children, topsByLane };
}

/** Height of a node's band: its own card (or the blockers in front of it) or its children's. */
function bandHeights(lane, tree, ownHeight, config) {
  const memo = new Map();
  const h = (id) => {
    if (memo.has(id)) return memo.get(id);
    const kids = tree.children.get(id) || [];
    const sum = kids.reduce((s, k) => s + h(k), 0) + Math.max(0, kids.length - 1) * config.rowGap;
    const v = Math.max(ownHeight(id), sum);
    memo.set(id, v);
    return v;
  };
  return h;
}

/**
 * Centre y per card in a lane: the bands stacked one after another, each
 * placed as a band (the node at its band's middle, its children stacked
 * inside). The start, the goals and DONE stand in columns no band uses, so
 * they sit on the lane's centre line without a row of their own — no empty
 * zone through the middle of the lane. Returns the ys and the lane's extent.
 */
export function placeLane(lane, laneTop, tree, ownHeight, centreHalf, config) {
  const tops = tree.topsByLane.get(lane.id) || [];
  const bandH = bandHeights(lane, tree, ownHeight, config);
  const gap = config.rowGap;
  const stackH = tops.reduce((s, id) => s + bandH(id), 0) + Math.max(0, tops.length - 1) * gap;
  const half = Math.max(stackH / 2, centreHalf);
  const centreY = laneTop + half;
  const ys = new Map();
  const place = (id, top) => {
    ys.set(id, top + bandH(id) / 2);
    let y = top;
    (tree.children.get(id) || []).forEach((k) => { place(k, y); y += bandH(k) + gap; });
  };
  let y = centreY - stackH / 2;
  tops.forEach((id) => { place(id, y); y += bandH(id) + gap; });
  return { ys, centreY, top: laneTop, bottom: centreY + half };
}

export { cardHeightFor };
